import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';

import { App } from '@/App';
import { ThemeProvider } from '@/design-system/ThemeProvider';
// Side-effect import: injects the backend base URL into the shared api
// singleton before any endpoint is called.
import '@/lib/api/base-url';
import { isApiError } from '@/lib/api/error';
import { AuthProvider } from '@/lib/auth/AuthContext';
import { UserAuthProvider } from '@/lib/auth/UserAuthContext';
import { installErrorReporter } from '@/lib/error-reporter';
import '@/styles/index.css';

installErrorReporter();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // 4xx responses won't change on retry — surface the error immediately.
        if (isApiError(error) && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <UserAuthProvider>
              <App />
            </UserAuthProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </ThemeProvider>
      {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  </React.StrictMode>,
);
